import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@hooks';
import { Stack, Typography } from '@esgian/esgianui';
import CanvasTimeSeriesChart from '@components/Charts/CanvasTimeSeriesChart/CanvasTimeSeriesChart';
import { TextWithTooltipIcon } from '@components/Display';
import { NoDataAvailable } from '@components/Display';

let tooltipText = (
  <Stack spacing={2}>
    <Typography variant={'caption'}>
      Detailed energy regeneration for the day selected. 
      The reused energy is consumed by the other drives on the VFD switchboard and is considered as free energy. 
      The burned energy is wasted energy because it is burned over the resistor banks via the brake choppers on the switchboard.
    </Typography>
  </Stack>
);

function DetailedRegeneratedEnergyDrawworks({
  chartRef,
  syncRefs,
  chartData,
  loading,
  highlightPeriod
}) {
  const {
    theme: { palette }
  } = useTheme();

  const { unit, series } = useMemo(() => {
    if (!chartData || !chartData.reused || !chartData.burned) return {};
    return {
      series: [
        {
          type: 'line',
          stack: 1,
          borderColor: palette.success.main,
          label: 'Reused',
          data: chartData.reused
        },
        {
          type: 'line',
          stack: 1,
          borderColor: palette.error.main,
          label: 'Burned',
          data: chartData.burned
        }
      ],
      unit: chartData.unit
    };
  }, [chartData]);

  return (
    <Stack spacing={2}>
      <TextWithTooltipIcon
        tooltipText={tooltipText}
        label={<Typography variant={'body2'}>Detailed energy regeneration in selection</Typography>}
      />
      {!loading && NoDataAvailable.hasNoData(series) ? (
        <NoDataAvailable height="310px" />
      ) : (
      <CanvasTimeSeriesChart
        highlightPeriod={highlightPeriod}
        syncRefs={syncRefs}
        chartRef={chartRef}
        height={'310px'}
        series={series}
        unit={unit}
        loading={loading}
        includeTooltipSum={true}
        id={'detailed-regenerated-energy-chart'}
        hideEmptyTooltipValues
        displayZeroTooltipValues={true}
        tooltipDatasetMaxHeightPx={300}
        positionTooltipAboveChart={false}
      />
      )}
    </Stack>
  );
}

DetailedRegeneratedEnergyDrawworks.propTypes = {
  loading: PropTypes.bool,
  syncRefs: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
  ),
  chartRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  highlightPeriod: PropTypes.shape({
    startIndex: PropTypes.number,
    endIndex: PropTypes.number
  }),
  chartData: PropTypes.shape({
    unit: PropTypes.string,
    reused: PropTypes.arrayOf(
        PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number
        })
      ),
    burned: PropTypes.arrayOf(
      PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number
      })
    ),
  })
};

DetailedRegeneratedEnergyDrawworks.defaultProps = {
  chartData: null,
  highlightPeriod: {},
  loading: false
};

export default DetailedRegeneratedEnergyDrawworks;